/**
 * LiveCountersBar: the always-visible bottom bar (PRD §9.6, §14.4).
 * Never buried in a menu — this is what turns "looks right" into "is
 * right" for a coordinator who needs to trust the numbers without
 * counting chairs by hand.
 *
 * Also surfaces a per-type inventory breakdown (used / available) behind
 * a toggle — round tables, rectangle tables, chairs, cocktail tables,
 * dance floors — using the same inventoryLimits + chair stock data the
 * validation engine already checks against, so the numbers shown here
 * can never drift from what the warnings panel is actually enforcing.
 */

import { useState } from 'react';
import { useEditorStore } from '../../store/useEditorStore';
import type { InventoryLimit } from '../../domain/types';
import { useChairStock } from '../../api/inventoryLimits';
import { WarningsList } from './WarningsList';

interface LiveCountersBarProps {
  guestCountTarget: number;
  inventoryLimits: InventoryLimit[];
}

const INVENTORY_DISPLAY: { type: string; label: string }[] = [
  { type: 'round_table', label: 'Round tables' },
  { type: 'rect_table_6', label: "6' rectangle tables" },
  { type: 'rect_table_8', label: "8' rectangle tables" },
  { type: 'cocktail_table', label: 'Cocktail tables' },
  { type: 'dance_floor_15', label: "15' dance floors" },
  { type: 'dance_floor_18', label: "18' dance floors" },
];

export function LiveCountersBar({ guestCountTarget, inventoryLimits }: LiveCountersBarProps) {
  const objects = useEditorStore((state) => state.objects);
  const validationIssues = useEditorStore((state) => state.validationIssues);
  const { data: chairStock } = useChairStock();
  // Only one of these two expandable panels can be open at a time — both
  // are absolutely positioned directly above this bar (bottom-full), so
  // having both open simultaneously would render them stacked
  // unreadably on top of each other.
  const [openPanel, setOpenPanel] = useState<'none' | 'inventory' | 'warnings'>('none');

  const totalSeats = objects.reduce((sum, o) => sum + (o.seats ?? 0), 0);
  const totalChairs = totalSeats; // chairs are derived 1:1 from seats in V1, never placed independently
  const tableTypes = ['round_table', 'rect_table_6', 'rect_table_8', 'head_table', 'sweetheart_table'];
  const totalTables = objects.filter((o) => tableTypes.includes(o.type)).length;

  const seatsMatch = totalSeats === guestCountTarget;

  // Per direct coordinator feedback, "info"-severity issues (e.g. two
  // tables a few inches closer than the recommended 18in — extremely
  // common in real, perfectly workable layouts) must NEVER count toward
  // the primary number a coordinator sees. Counting every issue
  // identically was exactly what trained coordinators to start ignoring
  // the badge altogether. Only warning + critical count here; info-tier
  // issues still exist and are visible inside the panel for anyone who
  // chooses to look, just never inflate the headline number.
  const criticalIssues = validationIssues.filter((i) => i.severity === 'critical');
  const warningIssues = validationIssues.filter((i) => i.severity === 'warning');
  const infoIssues = validationIssues.filter((i) => i.severity === 'info');
  const meaningfulCount = criticalIssues.length + warningIssues.length;
  const hasCritical = criticalIssues.length > 0;

  // While the real chair stock value is still loading, fall back to
  // showing just the used count with no "/ N" comparison rather than a
  // wrong or stale number — see CHAIR_STOCK note in domain/inventory.ts
  // for why this must come from the live database value, not a constant.
  const chairLimitDisplay = chairStock !== undefined ? `${totalChairs} / ${chairStock}` : String(totalChairs);
  const overChairLimit = chairStock !== undefined && totalChairs > chairStock;

  function countOfType(type: string): number {
    return objects.filter((o) => o.type === type).length;
  }

  function limitFor(type: string): number | undefined {
    return inventoryLimits.find((l) => l.itemType === type)?.maxQuantity;
  }

  // Only show rows for items that actually have a known venue limit, or
  // that are currently in use — an empty "0/0 donut walls" row would just
  // be noise, since donut walls etc. have no stock cap to track here.
  const visibleRows = INVENTORY_DISPLAY.filter(
    ({ type }) => limitFor(type) !== undefined || countOfType(type) > 0
  );

  // Three distinct visual states, not just "clear vs not": a real
  // safety/setup-blocking issue (critical) should read as genuinely
  // urgent, while ordinary warnings stay calm and informative — the
  // explicit goal being "feel like an experienced coordinator looking
  // over your shoulder, not a compiler pointing out every possible
  // technical issue."
  const badgeStyle = hasCritical
    ? 'ml-auto flex items-center gap-1.5 rounded-full bg-danger/10 px-3 py-1 font-body text-sm font-medium text-danger'
    : meaningfulCount > 0
      ? 'ml-auto flex items-center gap-1.5 rounded-full bg-warning/10 px-3 py-1 font-body text-sm text-warning'
      : 'ml-auto flex items-center gap-1.5 rounded-full bg-sage-100 px-3 py-1 font-body text-sm text-sage-400';

  const badgeText = hasCritical
    ? `\u26A0 ${criticalIssues.length} need${criticalIssues.length === 1 ? 's' : ''} attention`
    : meaningfulCount > 0
      ? `${meaningfulCount} worth a look`
      : '\u2713 All clear';

  return (
    <div className="relative border-t border-ivory-300 bg-white">
      <div className="flex flex-wrap items-center gap-6 px-4 py-2.5">
        <CounterStat
          label="Guests"
          value={`${totalSeats} / ${guestCountTarget}`}
          tone={seatsMatch ? 'good' : 'warn'}
        />
        <CounterStat label="Chairs" value={chairLimitDisplay} tone={overChairLimit ? 'warn' : 'neutral'} />
        <CounterStat label="Tables" value={String(totalTables)} />

        <button
          onClick={() => setOpenPanel((p) => (p === 'inventory' ? 'none' : 'inventory'))}
          className="font-body text-sm text-ink-500 underline-offset-2 hover:text-evergreen-700 hover:underline"
        >
          Inventory{openPanel === 'inventory' ? ' \u25B4' : ' \u25BE'}
        </button>

        <button onClick={() => setOpenPanel((p) => (p === 'warnings' ? 'none' : 'warnings'))} className={badgeStyle}>
          {badgeText}
        </button>
      </div>

      {openPanel === 'inventory' && (
        <div className="absolute bottom-full left-0 right-0 border-t border-ivory-300 bg-white px-4 py-3 shadow-elevated">
          <p className="mb-2 font-body text-xs uppercase tracking-wide text-ink-400">
            Inventory in use
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {visibleRows.map(({ type, label }) => {
              const used = countOfType(type);
              const limit = limitFor(type);
              const overLimit = limit !== undefined && used > limit;
              return (
                <div key={type} className="flex items-center justify-between gap-2 rounded border border-ivory-200 px-2 py-1.5">
                  <span className="font-body text-xs text-ink-500">{label}</span>
                  <span className={`font-body text-xs font-medium ${overLimit ? 'text-warning' : 'text-ink-700'}`}>
                    {used}
                    {limit !== undefined ? ` / ${limit}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {openPanel === 'warnings' && validationIssues.length > 0 && (
        <WarningsList
          criticalIssues={criticalIssues}
          warningIssues={warningIssues}
          infoIssues={infoIssues}
          onClose={() => setOpenPanel('none')}
        />
      )}
    </div>
  );
}

function CounterStat({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: 'good' | 'warn' | 'neutral';
}) {
  const valueColor =
    tone === 'good' ? 'text-evergreen-600' : tone === 'warn' ? 'text-warning' : 'text-ink-700';

  return (
    <div className="flex items-baseline gap-1.5">
      <span className="font-body text-xs uppercase tracking-wide text-ink-400">{label}</span>
      <span className={`font-display text-base font-medium ${valueColor}`}>{value}</span>
    </div>
  );
}
